/**
 * SYS-B-03 (Phase 4 C5) — Réconciliation bulletins ↔ comptes de paie.
 *
 * Schéma unique : les écritures attendues sont celles de `EcrituresPaieGenerator` (table de
 * correspondance `ComptesPaie`), comparées compte par compte (solde net débit − crédit) aux écritures
 * de paie du journal. Une correction d'une période clôturée (extourne + écriture de correction,
 * imputées sur une période ouverte) est rattachée à la période du bulletin par `periode_origine`.
 */
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Salarie } from '@shared/types/Salarie';
import { roundFCFA } from '@shared/utils/round';
import { COMPTES_PAIE_PAR_DEFAUT, type TableComptesPaie } from './ComptesPaie';
import { EcrituresPaieGenerator } from './EcrituresPaieGenerator';
import { PieceDesequilibreeError } from './EquilibrePiece';

export type EcartReconciliation = {
  compte: string;
  /** Solde net attendu (débit − crédit). */
  attendu: number;
  /** Solde net constaté (débit − crédit). */
  constate: number;
  ecart: number;
  sens: 'debit' | 'credit';
};

export type EtatReconciliationPaie = {
  periode: string;
  nb_bulletins: number;
  od_debit: number;
  od_credit: number;
  ecarts: EcartReconciliation[];
  /** Bulletins dont la pièce ne s'équilibre pas (génération refusée). */
  bulletins_desequilibres: string[];
  ok: boolean;
  attendu_par_compte: Record<string, { debit: number; credit: number }>;
  constate_par_compte: Record<string, { debit: number; credit: number }>;
};

function agreger(
  lignes: readonly Pick<Ecriture, 'compte' | 'debit' | 'credit'>[],
): Record<string, { debit: number; credit: number }> {
  const map: Record<string, { debit: number; credit: number }> = {};
  for (const l of lignes) {
    const cur = map[l.compte] ?? { debit: 0, credit: 0 };
    cur.debit = roundFCFA(cur.debit + (l.debit || 0));
    cur.credit = roundFCFA(cur.credit + (l.credit || 0));
    map[l.compte] = cur;
  }
  return map;
}

/** Écriture de paie, de correction ou d'extourne de paie rattachée à la période du bulletin. */
function estEcriturePaieDeLaPeriode(e: Ecriture, periode: string): boolean {
  const origine = e.periode_origine ?? e.periode;
  if (origine !== periode) return false;
  if (e.source === 'paie' || e.source === 'paie_correction') return true;
  return e.source === 'extourne' && e.journal === 'PA';
}

export function reconcilerPaiePeriode(
  bulletins: readonly Bulletin[],
  ecritures: readonly Ecriture[],
  periode: string,
  options: { salaries?: readonly Salarie[]; comptes?: TableComptesPaie } = {},
): EtatReconciliationPaie {
  const comptes = options.comptes ?? COMPTES_PAIE_PAR_DEFAUT;
  const duMois = bulletins.filter((b) => b.periode === periode);
  const attenduLignes: Ecriture[] = [];
  const bulletins_desequilibres: string[] = [];
  for (const b of duMois) {
    try {
      attenduLignes.push(
        ...EcrituresPaieGenerator.generer([b], options.salaries ?? [], { comptes }),
      );
    } catch (err) {
      if (!(err instanceof PieceDesequilibreeError)) throw err;
      bulletins_desequilibres.push(b.nom_complet ?? b.salarie_id);
    }
  }
  const attendu = agreger(attenduLignes);
  const constate = agreger(ecritures.filter((e) => estEcriturePaieDeLaPeriode(e, periode)));

  const ecarts: EcartReconciliation[] = [];
  const tousComptes = [...new Set([...Object.keys(attendu), ...Object.keys(constate)])].sort();
  for (const compte of tousComptes) {
    const a = attendu[compte] ?? { debit: 0, credit: 0 };
    const c = constate[compte] ?? { debit: 0, credit: 0 };
    const netAttendu = roundFCFA(a.debit - a.credit);
    const netConstate = roundFCFA(c.debit - c.credit);
    if (netAttendu !== netConstate) {
      ecarts.push({
        compte,
        attendu: netAttendu,
        constate: netConstate,
        ecart: roundFCFA(netConstate - netAttendu),
        sens: netConstate - netAttendu > 0 ? 'debit' : 'credit',
      });
    }
  }
  const od_debit = roundFCFA(attenduLignes.reduce((s, l) => s + l.debit, 0));
  const od_credit = roundFCFA(attenduLignes.reduce((s, l) => s + l.credit, 0));
  return {
    periode,
    nb_bulletins: duMois.length,
    od_debit,
    od_credit,
    ecarts,
    bulletins_desequilibres,
    ok: ecarts.length === 0 && bulletins_desequilibres.length === 0,
    attendu_par_compte: attendu,
    constate_par_compte: constate,
  };
}

export function resumeReconciliation(etat: EtatReconciliationPaie): string {
  if (etat.ok) {
    return `Réconciliation paie ${etat.periode} : OK (${etat.nb_bulletins} bulletin(s), Σ ${etat.od_debit} FCFA).`;
  }
  const detail = etat.ecarts.slice(0, 6).map((e) => `${e.compte} écart ${e.ecart}`).join(' ; ');
  const desequilibres =
    etat.bulletins_desequilibres.length > 0
      ? ` ${etat.bulletins_desequilibres.length} bulletin(s) déséquilibré(s).`
      : '';
  return `Réconciliation paie ${etat.periode} : ${etat.ecarts.length} écart(s) — ${detail}${desequilibres}`;
}
